import { BalanceData, Rarity } from "../data/balance";
import { Fix64 } from "../maths/fix64";
import { GearItem, GearOwner, affinityOf, itemPower, primaryStatOn } from "./gear";
import { StatUse, gearStatUse } from "./statUse";
import { mythicActiveOn } from "./mythic";

/**
 * An item's gear score on one owner (DESIGN §3.4, §9.4; FINISH_PLAN WP7 M6): the item's §7 score, counted the way the
 * owner would feel it. Two things change it by seat:
 * - affinity: on a tower that answers best to the item's primary stat the primary counts `affinityMult` times, so the
 *   score does too (floored to a whole number);
 * - a Mythic's effect works only on towers with its mechanic (Twin Shells needs a splash shot): elsewhere it does
 *   nothing, so it scores as a Legendary of the same level there (meta/mythic.ts mythicActiveOn);
 * - the primary counts as the stat it is on that owner (gear.ts statOn: a Hex Totem's damage is range there); one the
 *   owner still cannot use scores nothing (meta/statUse.ts).
 * The readiness targets (tools/balance/readiness.ts) and "Equip best" (app gearPlan.ts) both measure with it, so the
 * strip, the plan and the Armoury agree. Presentation arithmetic only: the sim reads GearBonus, never this.
 */
export function seatScore(balance: BalanceData, item: GearItem, owner: GearOwner): number {
  const G = balance.gear;
  const rarity = item.rarity === Rarity.Mythic && !mythicActiveOn(balance, item, owner) ? Rarity.Legendary : item.rarity;
  const base = itemPower(balance, { rarity, level: item.level });
  const stat = primaryStatOn(balance, item, owner);
  if (stat === affinityOf(balance, owner)) return Fix64.fromInt(base).mul(G.affinityMult).floorToInt();
  return gearStatUse(balance, owner, stat) === StatUse.None ? 0 : base;
}

/** Gear score of every equipped item, each on the owner wearing it (`seatScore`). */
export function wornSeatScore(balance: BalanceData, items: readonly GearItem[]): number {
  let sum = 0;
  for (const it of items) if (it.equippedTo !== null) sum += seatScore(balance, it, it.equippedTo);
  return sum;
}
